import { useEffect, useRef, useCallback } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import * as amplitude from "@amplitude/analytics-browser";
import { sessionReplayPlugin } from "@amplitude/plugin-session-replay-browser";

import { capitalizeAllStringWithSeparator, getLoggedUserData } from "utils";

export function useAmplitude() {
  const { isAuthenticated, isLoading } = useAuth0();
  const userData = getLoggedUserData();

  const apiKey = process.env.REACT_APP_AMPLITUDE_API_KEY ?? "";
  const initialized = useRef<boolean>(false);

  useEffect(() => {
    if (!apiKey || isLoading || !isAuthenticated || !userData || initialized.current) return;

    amplitude.add(sessionReplayPlugin({ sampleRate: 1 }));
    amplitude.init(apiKey, { "autocapture": true });

    if (userData?.email) {
      amplitude.setUserId(userData?.email);
    };

    const identifyObj = new amplitude.Identify()
      .set("email", userData?.email ?? "unknown")
      .set("account_id", userData?.tenant ?? "unknown")
      .set("module", "site_based")
      .set("user_role", userData?.roles?.map((role: string) => capitalizeAllStringWithSeparator(role, "_")).join(", ") ?? "viewer")
      .set("region", "N/A")
      .set("industry", "N/A");

    amplitude.identify(identifyObj);
    initialized.current = true;
  }, [isAuthenticated, userData, isLoading, apiKey]);

  const trackEvent = useCallback((eventName: string, pageTitle: string, eventProperties?: Record<string, any>) => {
    if (!initialized.current) return;
    amplitude.track(`Site-Based: ${eventName}`, {
      ...eventProperties,
      email: userData?.email,
      account_id: userData?.tenant,
      module: "site_based",
      "user_role": userData?.roles?.map((role: string) => capitalizeAllStringWithSeparator(role, "_")).join(", "),
      "Page Title": pageTitle,
    });
  }, [userData]);

  return { isAuthenticated, trackEvent };
};
